import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { isFuture } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Flex, Margin } from '@/@bothrs-ui/web/components/layout'
import { MediaQuery } from '@/@bothrs-ui/web/components/media-query/MediaQuery'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Dot } from '@/@bothrs-ui/web/components/primitives/dot/Dot'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { SelectMealCard } from '@/modules/app/components/select-meal-card/SelectMealCard'
import { getEncounterSpecialisation } from '@/modules/encounter/utils/encounter-mapping'
import { RelatedItems } from '@/modules/shared/components/related-items/RelatedItems'
import { navigateMaps } from '@/modules/shared/helpers/navigation'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { useMealLink } from '@/utils/navigation'
import { EncounterContact } from '../../components/encounter-contact/EncounterContact'
import { EncounterInfo } from '../../components/encounter-info/EncounterInfo'
import { EncounterNotes } from '../../components/encounter-notes/EncounterNotes'
import { useEncounters } from '../../hooks/useEncounters'
import { StContainer, StContent, StHeaderWrapper } from './EncounterDetailPage.styled'

const EncounterDetailPage = () => {
  const navigate = useNavigate()
  // Hide navigation on native devices
  useHideNativeNavigation()

  const theme = useTheme()
  const { id } = useParams()

  const {
    getEncounterById: {
      execute: getEncounterById,
      value: encounter,
      isLoading: encounterIsLoading,
      error: encounterError,
    },
  } = useEncounters()

  const mealLink = useMealLink()?.url

  // Template Variables
  // ------------------------------------------------------------------------- /
  const address = useMemo(() => encounter?.meta?.location?.address, [encounter])

  const title = useMemo(() => {
    const intake = i18n._(activityTypeTranslations.Intake!)
    const specialisation = getEncounterSpecialisation(encounter)

    if (specialisation) {
      return `${intake}: ${specialisation}`
    }

    return intake
  }, [encounter])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (id) {
      getEncounterById(Number(id))
    }
  }, [id])

  useEffect(() => {
    if (encounterError && !encounterIsLoading) {
      navigate('/timeline')
    }
  }, [encounterError, encounterIsLoading, navigate])

  return (
    <>
      {encounterIsLoading ? null : (
        <ContentPageLayout
          noPadding
          header={
            <StHeaderWrapper>
              <AppHeader
                backgroundColor={theme.Colors.Background.White}
                primaryAction={{
                  label: i18n._(globalDefinitions.goBack),
                  icon: ['far', 'chevron-left'],
                  onClick: () => {
                    navigate('/timeline')
                  },
                }}
                secondaryAction={
                  encounter && address && isFuture(new Date(encounter.start))
                    ? {
                        label: t({
                          id: 'encounters.detail.navigation-button',
                          comment: 'Navigate to encounter',
                          message: 'Navigeer naar je opname',
                        }),
                        icon: ['fal', 'location-dot'],
                        onClick: () => {
                          navigateMaps(address)
                        },
                      }
                    : undefined
                }
              />
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Flex alignItems={'center'}>
                  <Dot color={theme.Colors.Category.Intake} />
                  <Margin mr={theme.UI.Spacing.Space2} />
                  <H3 noMargin>{title}</H3>
                </Flex>
              </Flex>
              <Line />
            </StHeaderWrapper>
          }
        >
          <StContainer>
            <StContent>
              {/* Title - visible on desktop only (on mobile it's in the header) */}
              <MediaQuery query={breakpoints.fromM}>
                <Flex justifyContent={'space-between'} alignItems={'center'}>
                  <Flex alignItems={'center'}>
                    <Dot color={theme.Colors.Category.Intake} />
                    <Margin mr={theme.UI.Spacing.Space2} />
                    <H3 noMargin>{title}</H3>
                  </Flex>
                </Flex>
                <Line />
              </MediaQuery>
              <EncounterInfo encounter={encounter} />
              {!!encounter?.meta?.notes?.length && (
                <>
                  <Line />
                  <EncounterNotes encounter={encounter} />
                </>
              )}

              <Line />
              {mealLink ? (
                <SelectMealCard onClick={() => mixpanel.track('appointment_manage_meals')} />
              ) : (
                <EncounterContact
                  encounter={encounter}
                  onPhoneClick={() => mixpanel.track('appointment_contact', { type: 'phone', origin: 'detail' })}
                />
              )}

              {encounter?.trigger.trim() && <RelatedItems trigger={encounter?.trigger} />}
              <Margin my={theme.UI.Spacing.Space6} />
            </StContent>
          </StContainer>
        </ContentPageLayout>
      )}
    </>
  )
}

export { EncounterDetailPage }
